import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './MainLandingPage.css';

const featureCards = [
  {
    title: '랭킹 분석',
    description: '지역, 입지, 수급 흐름을 기준으로 투자 우선순위를 빠르게 정리합니다.',
    points: ['유망 단지 상위 랭킹', '필터 기반 후보 압축', '관심 단지 즉시 비교'],
  },
  {
    title: '단지 비교',
    description: '복수 단지의 가격, 전세가율, 거래 흐름을 한 화면에서 정밀 비교합니다.',
    points: ['지표별 on/off', '최고가/저점 기준 파악', '차트 중심 의사결정'],
  },
  {
    title: 'KB 데이터',
    description: '매매지수, 전세지수, 매수심리를 함께 읽어 시장 체력과 전환 구간을 포착합니다.',
    points: ['지수/증감 토글 분석', '지역별 다중 비교', '심리 지표 해석 지원'],
  },
  {
    title: '실거래 기반 검증',
    description: '주간 데이터와 실거래 데이터를 연결해 실제 체결 강도를 검증합니다.',
    points: ['거래량 추세 확인', '매물 대비 체결 강도', '기간 줌인/줌아웃 분석'],
  },
];

const mockupCards = [
  {
    title: '랭킹 그리드 화면',
    caption: '조건 필터 + 항목 설정 + 열 순서 커스터마이징',
    badge: 'UI Mockup',
    imageUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80',
  },
  {
    title: '단지 비교 차트',
    caption: '가격/전세가율/거래량을 한 번에 비교',
    badge: 'Chart Mockup',
    imageUrl: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=1200&q=80',
  },
  {
    title: 'KB 지수 대시보드',
    caption: '지수와 증감을 전환해 추세와 모멘텀 동시 확인',
    badge: 'Insight Mockup',
    imageUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80',
  },
];

const workflow = [
  {
    step: '01',
    title: '후보 발굴',
    desc: '랭킹과 필터를 활용해 투자 검토 단지를 빠르게 좁힙니다.',
  },
  {
    step: '02',
    title: '정밀 비교',
    desc: '여러 단지를 차트와 지표로 비교해 우선순위를 정합니다.',
  },
  {
    step: '03',
    title: '타이밍 판단',
    desc: '지수/심리/거래량 조합으로 진입 시점을 검증합니다.',
  },
];

const sectionNav = [
  { id: 'landing-feature', label: '기능' },
  { id: 'landing-mockup', label: '화면' },
  { id: 'landing-workflow', label: '분석흐름' },
  { id: 'landing-summary', label: '바로가기' },
];

function MainLandingPage() {
  const { isLoggedIn } = useAuth();
  const sectionRefs = useRef({});
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState(sectionNav[0].id);
  const [visibleSections, setVisibleSections] = useState({});

  useEffect(() => {
    const onScroll = () => {
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const value = Math.min(100, Math.max(0, (window.scrollY / max) * 100));
      setScrollProgress(value);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  useEffect(() => {
    const ids = sectionNav.map((item) => item.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionId = entry.target.getAttribute('id');
          if (!sectionId) return;

          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({ ...prev, [sectionId]: true }));
            setActiveSection(sectionId);
          }
        });
      },
      { threshold: 0.35 }
    );

    ids.forEach((id) => {
      const node = sectionRefs.current[id];
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, []);

  const moveToSection = (id) => {
    const node = sectionRefs.current[id];
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const revealClass = (id) => `reveal-section ${visibleSections[id] ? 'is-visible' : ''}`;

  return (
    <main className="main-landing" aria-label="메인 랜딩 페이지">
      <div className="landing-scroll-progress" aria-hidden="true">
        <span style={{ width: `${scrollProgress}%` }} />
      </div>

      <section className="hero-section reveal-section is-visible">
        <div className="hero-badge">아파트 투자 분석 플랫폼</div>
        <h1>부동산 투자 판단을 더 쉽고 명확하게</h1>
        <p>
          RANKER는 랭킹, 실거래, KB 지표를 하나의 흐름으로 연결해
          누구나 근거 있는 투자 분석을 할 수 있도록 돕는 플랫폼입니다.
        </p>
        <div className="hero-actions">
          {isLoggedIn ? (
            <>
              <Link to="/home" className="hero-button primary">대시보드 시작</Link>
              <Link to="/ranking" className="hero-button secondary">랭킹 바로보기</Link>
            </>
          ) : (
            <>
              <Link to="/login" className="hero-button primary">로그인</Link>
              <Link to="/signup" className="hero-button secondary">회원가입</Link>
            </>
          )}
        </div>
      </section>

      <nav className="landing-quick-nav" aria-label="랜딩 섹션 이동">
        {sectionNav.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => moveToSection(item.id)}
            className={activeSection === item.id ? 'active' : ''}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <section className={`trust-strip ${revealClass('landing-feature')}`}>
        <article>
          <strong>데이터 신뢰성</strong>
          <span>실거래와 주간 지표를 교차 검증</span>
        </article>
        <article>
          <strong>분석 효율성</strong>
          <span>한 화면에서 비교와 판단까지</span>
        </article>
        <article>
          <strong>의사결정 명확성</strong>
          <span>차트 중심으로 흐름과 시점을 파악</span>
        </article>
      </section>

      <section
        id="landing-feature"
        ref={(node) => { sectionRefs.current['landing-feature'] = node; }}
        className={`feature-section section-tone-a ${revealClass('landing-feature')}`}
        aria-label="서비스 기능 소개"
      >
        <header className="section-head">
          <h2>현재 서비스 기능</h2>
          <p>투자자가 실제로 자주 쓰는 분석 흐름에 맞춰 기능을 구성했습니다.</p>
        </header>
        <div className="feature-grid">
          {featureCards.map((feature) => (
            <article className="feature-card" key={feature.title}>
              <div className="feature-card-head">
                <span className="feature-card-dot" aria-hidden="true" />
                <h3>{feature.title}</h3>
              </div>
              <p>{feature.description}</p>
              <ul>
                {feature.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section
        id="landing-mockup"
        ref={(node) => { sectionRefs.current['landing-mockup'] = node; }}
        className={`mockup-section section-tone-b ${revealClass('landing-mockup')}`}
        aria-label="화면 및 차트 목업"
      >
        <header className="section-head">
          <h2>화면과 차트를 통한 직관적 분석</h2>
          <p>핵심 화면과 차트 중심으로 기능 가치를 빠르게 전달합니다.</p>
        </header>
        <div className="mockup-grid">
          {mockupCards.map((card) => (
            <article className="mockup-card" key={card.title}>
              <div className="mockup-tag">{card.badge}</div>
              <div className="mockup-image-wrap">
                <img src={card.imageUrl} alt={card.title} loading="lazy" />
              </div>
              <h3>{card.title}</h3>
              <p>{card.caption}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        id="landing-workflow"
        ref={(node) => { sectionRefs.current['landing-workflow'] = node; }}
        className={`workflow-section section-tone-a ${revealClass('landing-workflow')}`}
        aria-label="사용 흐름"
      >
        <header className="section-head">
          <h2>투자 분석이 쉬워지는 3단계</h2>
          <p>복잡한 데이터를 단계별로 정리해 빠르게 행동할 수 있게 만듭니다.</p>
        </header>
        <div className="workflow-grid">
          {workflow.map((item) => (
            <article className="workflow-card" key={item.step}>
              <span className="workflow-step">{item.step}</span>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        id="landing-summary"
        ref={(node) => { sectionRefs.current['landing-summary'] = node; }}
        className={`summary-section section-tone-b ${revealClass('landing-summary')}`}
        aria-label="바로가기 섹션"
      >
        <div className="summary-card">
          <h3>랭킹부터 비교까지 한 번에</h3>
          <p>후보 발굴부터 단지 비교, 차트 분석까지 한 흐름으로 이어집니다.</p>
          <Link to="/ranking" className="summary-link">랭킹 보러가기</Link>
        </div>
        <div className="summary-card">
          <h3>데이터 기반 투자 루틴 구축</h3>
          <p>KB 지표와 실거래 데이터를 함께 보며 시장 방향성과 타이밍을 점검하세요.</p>
          <Link to="/kb-data" className="summary-link">데이터 보러가기</Link>
        </div>
      </section>

      <section className="final-cta reveal-section is-visible" aria-label="최종 콜투액션">
        <h2>좋은 투자 판단은 좋은 데이터 플랫폼에서 시작됩니다</h2>
        <p>RANKER에서 복잡한 부동산 데이터를 이해하기 쉬운 분석 화면으로 바꿔보세요.</p>
        <div className="hero-actions">
          {isLoggedIn ? (
            <Link to="/home" className="hero-button primary">지금 분석 시작</Link>
          ) : (
            <>
              <Link to="/signup" className="hero-button primary">무료로 시작하기</Link>
              <Link to="/login" className="hero-button secondary">로그인</Link>
            </>
          )}
        </div>
      </section>
    </main>
  );
}

export default MainLandingPage;
